import { css } from "@emotion/react";

import type { ButtonColorScheme } from "./types/ButtonColorScheme";
import type { ButtonSize } from "./types/ButtonSize";
import type { ButtonVariant } from "./types/ButtonVariant";
import type { SerializedStyles } from "@emotion/react";

const sizeStylesMap: Record<ButtonSize, SerializedStyles> = {
  xs: css`
    height: 24px;
    padding-left: 8px;
    padding-right: 8px;
    column-gap: 6px;
    font-size: 12px;
    line-height: 16px;
  `,
  sm: css`
    height: 32px;
    padding-left: 12px;
    padding-right: 12px;
    column-gap: 8px;
    font-size: 14px;
    line-height: 20px;
  `,
  md: css`
    height: 40px;
    padding-left: 16px;
    padding-right: 16px;
    column-gap: 8px;
    font-size: 16px;
    line-height: 24px;
  `,
  lg: css`
    height: 48px;
    padding-left: 24px;
    padding-right: 24px;
    column-gap: 8px;
    font-size: 18px;
    line-height: 28px;
  `,
};

const disabledStyle = css`
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const solidButtonBaseStyle = css`
  border: none;
  ${disabledStyle}
`;

const solidVariantStylesMap: Record<ButtonColorScheme, SerializedStyles> = {
  primary: css`
    ${solidButtonBaseStyle}
    background-color: #000;
    color: #fff;
  `,
  opposite: css`
    ${solidButtonBaseStyle}
    background-color: #fff;
    color: #000;
  `,
  critical: css`
    ${solidButtonBaseStyle}
    background-color: #e53e3e;
    color: #fff;
  `,
  warning: css`
    ${solidButtonBaseStyle}
    background-color: #d69e2e;
    color: #000;
  `,
};

const outlineButtonBaseStyle = css`
  background-color: transparent;
  ${disabledStyle}
`;

const outlineVariantStylesMap: Record<ButtonColorScheme, SerializedStyles> = {
  primary: css`
    ${outlineButtonBaseStyle}
    color: #000;
    border: 1px solid #000;
  `,
  opposite: css`
    ${outlineButtonBaseStyle}
    color: #fff;
    border: 1px solid #fff;
  `,
  critical: css`
    ${outlineButtonBaseStyle}
    color: #e53e3e;
    border: 1px solid #e53e3e;
  `,
  warning: css`
    ${outlineButtonBaseStyle}
    color: #d69e2e;
    border: 1px solid #d69e2e;
  `,
};

const ghostButtonBaseStyle = css`
  background-color: transparent;
  border: none;
  ${disabledStyle}
`;

const ghostVariantStylesMap: Record<ButtonColorScheme, SerializedStyles> = {
  primary: css`
    ${ghostButtonBaseStyle}
    color: #000;
  `,
  opposite: css`
    ${ghostButtonBaseStyle}
    color: #fff;
  `,
  critical: css`
    ${ghostButtonBaseStyle}
    color: #e53e3e;
  `,
  warning: css`
    ${ghostButtonBaseStyle}
    color: #d69e2e;
  `,
};

const variantMap = {
  solid: solidVariantStylesMap,
  outline: outlineVariantStylesMap,
  ghost: ghostVariantStylesMap,
};

const baseIconStyle = css`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const iconSizeMap: Record<ButtonSize, SerializedStyles> = {
  xs: css`
    ${baseIconStyle}
    width: 12px;
    height: 12px;
  `,
  sm: css`
    ${baseIconStyle}
    width: 16px;
    height: 16px;
  `,
  md: css`
    ${baseIconStyle}
    width: 20px;
    height: 20px;
  `,
  lg: css`
    ${baseIconStyle}
    width: 24px;
    height: 24px;
  `,
};

export const baseButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  user-select: none;
  position: relative;
`;

export const getButtonStyles = (
  size: ButtonSize,
  variant: ButtonVariant,
  colorScheme: ButtonColorScheme
) => css`
  ${baseButtonStyle};
  ${sizeStylesMap[size]};
  ${variantMap[variant][colorScheme]};
`;

export const getIconStyle = (size: ButtonSize) => iconSizeMap[size];
